import { Download, Send } from "@mui/icons-material";
import { MainLayoutContainer } from "components/Layout";
import { ResponsiveButton } from "components/Button";
import { StrugglingFlagChip } from "components/StrugglingFlag/StrugglingFlagChip";
import { useStrugglingLearnerRoster } from "hooks/useStrugglingLearnerRoster";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCustomUserDetail } from "./hook/useCustomUserDetail";
import { useLearnerHomeData } from "./hook/useLearnerHomeData";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ROLES } from "types/user";
import { useParams } from "react-router-dom";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { EPACompetencyRequirements } from "pages/Home/components";
import { FeedbackOverview } from "pages/Home/components/FeedbackOverview";
import { RecentFeedbackActivity } from "pages/Home/components/RecentFeedbackActivity";
import {
  downloadStudentEducationRecords,
  DisclosureParty,
  releaseStudentEducationRecords,
} from "services/educationRecords";
import { recordEducationRecordRead } from "services/educationRecordAccessLog";
import {
  LEARNER_ACCESS_MESSAGE,
  LEARNER_ACCESS_TITLE,
} from "utils/feedback";

type CustomUserDetailProps = {
  role: ROLES;
};

const ORG_STAFF_ROLES = new Set([ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER]);

export const CustomUserDetail: FC<CustomUserDetailProps> = ({ role }) => {
  const { loading, userInfo, role: userRole, effectiveOrganizationId } =
    useCustomUserDetail();

  const { userId: learnerUserId } = useParams<{ userId?: string }>();
  const [
    selectedRequirementId,
    setSelectedRequirementId,
  ] = useState<string>("ALL");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseParty, setReleaseParty] = useState<DisclosureParty>("parent");
  const [releaseRecipient, setReleaseRecipient] = useState("");
  const [releasePurpose, setReleasePurpose] = useState("");

  const canExportEducationRecords =
    role === ROLES.STUDENT &&
    Boolean(learnerUserId) &&
    ORG_STAFF_ROLES.has(userRole as ROLES);
  const canViewStrugglingFlags = canExportEducationRecords;
  const { strugglingByStudentId } = useStrugglingLearnerRoster(
    canViewStrugglingFlags ? effectiveOrganizationId ?? undefined : undefined,
    canViewStrugglingFlags ? learnerUserId : undefined,
  );
  const strugglingSummary = learnerUserId
    ? strugglingByStudentId.get(learnerUserId)
    : undefined;

  useEffect(() => {
    if (role === ROLES.STUDENT && learnerUserId) {
      recordEducationRecordRead({
        resourceType: "student_overview",
        resourceId: learnerUserId,
      });
    }
  }, [role, learnerUserId]);

  const onExportEducationRecords = useCallback(async () => {
    if (!learnerUserId || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      await downloadStudentEducationRecords(learnerUserId);
    } catch {
      setExportError("Could not download education records. Try again.");
    } finally {
      setExporting(false);
    }
  }, [learnerUserId, exporting]);

  const onReleaseEducationRecords = useCallback(async () => {
    if (!learnerUserId || exporting) return;
    if (!releaseRecipient.trim() || !releasePurpose.trim()) {
      setExportError("Recipient and purpose are required.");
      return;
    }
    setExporting(true);
    setExportError(null);
    try {
      await releaseStudentEducationRecords(learnerUserId, {
        party: releaseParty,
        recipient: releaseRecipient.trim(),
        purpose: releasePurpose.trim(),
      });
      setReleaseOpen(false);
      setReleaseRecipient("");
      setReleasePurpose("");
    } catch {
      setExportError(
        "Could not release education records. The copy was not sent.",
      );
    } finally {
      setExporting(false);
    }
  }, [
    exporting,
    learnerUserId,
    releaseParty,
    releasePurpose,
    releaseRecipient,
  ]);
  const {
    data: learnerHomeData,
    statsData: learnerStatsData,
    loading: learnerHomeLoading,
  } = useLearnerHomeData(learnerUserId);

  const learnerAsUser = userInfo
    ? { ...userInfo, role: ROLES.STUDENT }
    : undefined;

  const requirementOptions = useMemo<{ id: string; name: string }[]>(() => {
    const reqs = learnerHomeData?.allRequirements ?? [];
    return reqs.map((r: any) => ({
      id: r.requirement_id,
      name: r.name ?? "",
    }));
  }, [learnerHomeData?.allRequirements]);

  const filteredStatsData = useMemo(() => {
    if (!learnerStatsData) return null;
    if (selectedRequirementId === "ALL") return learnerStatsData;
    return learnerStatsData.filter(
      (f: any) =>
        f.feedback_skills?.[0]?.requirement_id === selectedRequirementId,
    );
  }, [learnerStatsData, selectedRequirementId]);

  const filteredFeedbacks = useMemo(() => {
    const list = learnerHomeData?.feedbacks ?? [];
    if (selectedRequirementId === "ALL") return list;
    return list.filter((f: any) =>
      f.feedback_skills?.some(
        (s: any) => s.requirement?.requirement_id === selectedRequirementId,
      ),
    );
  }, [learnerHomeData?.feedbacks, selectedRequirementId]);

  const filteredDataForRequirements = useMemo(() => {
    if (!learnerHomeData) return learnerHomeData;
    if (selectedRequirementId === "ALL") return learnerHomeData;
    const matchId = (r: any) => r?.requirement_id === selectedRequirementId;
    return {
      ...learnerHomeData,
      epaRequirements: (learnerHomeData.epaRequirements ?? []).filter(matchId),
      competencyRequirements: (learnerHomeData.competencyRequirements ?? []).filter(matchId),
      iterRequirements: (learnerHomeData.iterRequirements ?? []).filter(matchId),
      feedbacksApproved: learnerHomeData.feedbacksApproved
        ? { [selectedRequirementId]: learnerHomeData.feedbacksApproved[selectedRequirementId] }
        : {},
      feedbacksByRequirementId: learnerHomeData.feedbacksByRequirementId?.[selectedRequirementId]
        ? { [selectedRequirementId]: learnerHomeData.feedbacksByRequirementId[selectedRequirementId] }
        : {},
      feedbackStatsByRequirementId: learnerHomeData.feedbackStatsByRequirementId?.[selectedRequirementId]
        ? { [selectedRequirementId]: learnerHomeData.feedbackStatsByRequirementId[selectedRequirementId] }
        : {},
    };
  }, [learnerHomeData, selectedRequirementId]);

  return (
    <MainLayoutContainer
      title={`${userInfo?.name ?? "User"} Details`}
      loading={loading}
      breadcrumb={
        userRole === ROLES.COACH
          ? BREADCRUMB_PAGE.LEARNERS
          : BREADCRUMB_PAGE.ORGANIZATION_LEARNERS
      }
      buttonActions={
        canExportEducationRecords ? (
          <Stack direction="row" spacing={1}>
            <ResponsiveButton
              variant="outlined"
              icon={<Download />}
              onClick={() => {
                void onExportEducationRecords();
              }}
            >
              {exporting ? "Exporting…" : "Export education records"}
            </ResponsiveButton>
            <ResponsiveButton
              variant="outlined"
              icon={<Send />}
              onClick={() => setReleaseOpen(true)}
            >
              Release a copy
            </ResponsiveButton>
          </Stack>
        ) : null
      }
    >
      {!loading && !userInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {LEARNER_ACCESS_TITLE}
          </Typography>
          <Typography variant="body2">{LEARNER_ACCESS_MESSAGE}</Typography>
        </Alert>
      )}
      {exportError && (
        <Box py={1}>
          <Typography color="error" variant="body2">
            {exportError}
          </Typography>
        </Box>
      )}
      <Dialog
        open={releaseOpen}
        onClose={() => setReleaseOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Release education records</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This sends a copy outside school officials. VetNebula records who
            received it and why before the file downloads. Do not redisclose
            that copy.
          </Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel id="release-party-label">Recipient type</InputLabel>
            <Select
              labelId="release-party-label"
              label="Recipient type"
              value={releaseParty}
              onChange={(event) =>
                setReleaseParty(event.target.value as DisclosureParty)
              }
            >
              <MenuItem value="parent">Parent or guardian</MenuItem>
              <MenuItem value="other_school">Another school</MenuItem>
              <MenuItem value="other">Someone else</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="dense"
            label="Recipient"
            value={releaseRecipient}
            onChange={(event) => setReleaseRecipient(event.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Purpose"
            multiline
            minRows={2}
            value={releasePurpose}
            onChange={(event) => setReleasePurpose(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              exporting || !releaseRecipient.trim() || !releasePurpose.trim()
            }
            onClick={() => {
              void onReleaseEducationRecords();
            }}
          >
            Record and download
          </Button>
        </DialogActions>
      </Dialog>
      {learnerHomeLoading && !learnerHomeData && (
        <Box py={2}>
          <Typography color="text.secondary">Loading learner overview…</Typography>
        </Box>
      )}
      {learnerHomeData && learnerAsUser && (
        <Stack spacing={2} sx={{ mb: 4 }}>
          {canViewStrugglingFlags && (
            <Box>
              <Typography variant="body1" component="div">
                <strong>Situation:</strong>{" "}
                <StrugglingFlagChip
                  summary={strugglingSummary}
                  showReasons
                  orgId={effectiveOrganizationId ?? undefined}
                />
              </Typography>
            </Box>
          )}
          {requirementOptions.length > 0 && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">Filter by requirement:</Typography>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel id="learner-detail-requirement-filter-label">
                    Requirement
                  </InputLabel>
                  <Select
                    labelId="learner-detail-requirement-filter-label"
                    id="learner-detail-requirement-filter"
                    value={selectedRequirementId}
                    label="Requirement"
                    onChange={(e) =>
                      setSelectedRequirementId(e.target.value as string)
                    }
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    {requirementOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          )}
          <EPACompetencyRequirements
            data={filteredDataForRequirements}
            userRole={ROLES.STUDENT}
          />
          {filteredStatsData && (
            <FeedbackOverview
              data={filteredStatsData}
              epaCompletionRate={learnerHomeData.epaCompletionRate}
            />
          )}
          {filteredFeedbacks.length > 0 && (
            <RecentFeedbackActivity
              feedbacks={filteredFeedbacks}
              user={learnerAsUser}
              organizationId={effectiveOrganizationId ?? undefined}
              allRequirements={learnerHomeData.allRequirements ?? []}
              viewAllTo={
                effectiveOrganizationId && learnerUserId
                  ? `/organization/${effectiveOrganizationId}/all-feedback/${learnerUserId}`
                  : undefined
              }
            />
          )}
        </Stack>
      )}
    </MainLayoutContainer>
  );
};
